import { authResponseSchema, hasAccessToken, transportError } from "@sagot/api-client";
import { CONSOLE_PATHS } from "@sagot/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { clearPendingAuth, safeNext, writePendingAuth } from "../session/pending-auth";
import { useClient } from "../session/session-provider";

export interface SignInContext {
  email: string;
  /** Where to go after sign-in (validated same-app path). */
  next?: string | null | undefined;
}

/**
 * Applies any sign-in step response (`AuthResponse`): stores the session, or routes to the
 * pending 2FA / e-mail verification step. Parsed with zod at this boundary.
 */
export function useCompleteSignIn(): (body: unknown, context: SignInContext) => Promise<void> {
  const client = useClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useCallback(
    async (body, { email, next }) => {
      const parsed = authResponseSchema.safeParse(body);
      if (!parsed.success) throw transportError("parse");
      const response = parsed.data;
      const target = safeNext(next);
      if (hasAccessToken(response)) {
        clearPendingAuth();
        queryClient.clear();
        client.session.setSession(response);
        await navigate(target ?? "/", { replace: true });
        return;
      }
      if (response.status === "two_factor_required" && response.challenge_id) {
        writePendingAuth({ kind: "two-factor", challengeId: response.challenge_id, email, next: target });
        await navigate(CONSOLE_PATHS.twoFactor);
        return;
      }
      writePendingAuth({ kind: "verify-email", email });
      await navigate(`${CONSOLE_PATHS.verifyEmail}?email=${encodeURIComponent(email)}`);
    },
    [client, navigate, queryClient],
  );
}
